import { Component, Input, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { OnDestroy } from '@angular/core';
import { AppState } from 'src/app/store/app.states';
import { ComponentBase } from 'src/app/views/component-base';
import { FormControlState, FormGroupState, MarkAsTouchedAction, NgrxFormsModule } from 'ngrx-forms';
import { AppButtonComponent, EntryFieldAddressAccurityComponent, EntryFieldComponent, EntryFieldDateComponent, EntryFieldDropdownComponent, Helper } from '@ifaa-components/ui-components';
import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import { commonState_IsSending, commonState_LatestJourney } from 'src/app/store/common/common.selectors';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoToStepAction } from '../../actions';
import { GetGenderOptionsRequestAction, ResetDataSavedAction, SaveDetailsRequestAction, UpdateDetailsFieldRequestAction } from './actions';
import { details_DataSaved, details_Form, details_Genders, details_HasError, details_IsLoading } from './selectors';
import { SetDataFromDetailsStepAction } from '../nmj-initial-data/actions';
import { AppListDirective } from 'src/app/directives/list.directive';
import { SetMobileAction } from '../nmj-mobile-verification/actions';
import { AppButtonDirective } from 'src/app/directives/appbutton.directive';
import { PersonalDetailsModel } from 'src/app/model/member-details.model';

@Component({
    selector: 'app-nmj-details',
    templateUrl: './nmj-details.component.html',
    styleUrls: ['./nmj-details.component.scss'],
    imports: [
        AsyncPipe,
        AppButtonComponent,
        EntryFieldComponent,
        NgIf,
        NgrxFormsModule,
        ReactiveFormsModule,
        FormsModule,
        EntryFieldAddressAccurityComponent,
        EntryFieldDateComponent,
        EntryFieldDropdownComponent,
        NgTemplateOutlet,
        AppListDirective,
        AppButtonDirective
    ],
})
export class NmjDetailsComponent extends ComponentBase implements OnInit, OnDestroy {
    @Input() set step(value: number) {
        this.stepIndex = value;
    }

    isLoading$ = this.store.pipe(select(commonState_IsSending));
    localBlock$ = this.store.pipe(select(details_IsLoading));
    journeySteps$ = this.store.pipe(select(commonState_LatestJourney));
    dataSaved$ = this.store.pipe(select(details_DataSaved));
    form$ = this.store.pipe(select(details_Form));
    genders$ = this.store.pipe(select(details_Genders));
    hasError$ = this.store.pipe(select(details_HasError));

    helper: Helper = new Helper();
    stepIndex: number = 0;
    nextStep: number = 0;
    previousStep: number = 0;
    today: Date = new Date();
    timeout: ReturnType<typeof setTimeout> = null;

    constructor(public store: Store<AppState>) {
        super();
    }

    async ngOnInit() {
        super.ngOnInitBase();

        this.sub = this.journeySteps$.subscribe(async x => {
            if (x) {
                this.nextStep = (await this.helper.getValue(this.journeySteps$)).journeySteps.filter(x => x.journeyStep > this.stepIndex)[0]?.journeyStep ?? -1;
                this.previousStep = (await this.helper.getValue(this.journeySteps$)).journeySteps.filter(x => x.journeyStep < this.stepIndex).sort((a, b) => b.journeyStep - a.journeyStep)[0].journeyStep;
            }
        })

        this.sub = this.dataSaved$.subscribe(async x => {
            if (x) {
                var data = (await this.helper.getValue(this.form$)).value;

                // set data for initial step in case there were changes
                this.store.dispatch(SetDataFromDetailsStepAction({ data }))

                // set mobile for next step
                this.store.dispatch(SetMobileAction({ mobile: data.mobile }));

                // go next step
                this.store.dispatch(GoToStepAction({ index: this.nextStep }));

                // reset data saved prop
                this.store.dispatch(ResetDataSavedAction());
            }
        })

        this.sub = this.genders$.subscribe(x => {
            if (!x) {
                this.store.dispatch(GetGenderOptionsRequestAction());
            }
        })
    }

    ngOnDestroy(): void {
        super.ngOnDestroyBase();
    }

    async onMoveNextClick(
        form: FormGroupState<any>,
    ) {
        if (form.isInvalid) {
            this.store.dispatch(new MarkAsTouchedAction(form.id));
            return;
        }
        var data = (await this.helper.getValue(this.form$)).value;

        this.store.dispatch(
            SaveDetailsRequestAction({
                step: this.nextStep,
                field: 'page-change',
                data
            })
        );
    }

    async onMoveBackClick() {
        this.store.dispatch(GoToStepAction({ index: this.previousStep }))
    }

    async onInputValueChanged(
        input: FormGroupState<any> | FormControlState<any>,
        data: PersonalDetailsModel,
    ) {
        // debounce save so we arent spamming the api with cancelled requests
        clearTimeout(this.timeout);

        this.timeout = setTimeout(() => {
            this.store.dispatch(
                UpdateDetailsFieldRequestAction({
                    step: this.stepIndex,
                    field: input.id,
                    data: data,
                })
            )
        }, 500)
    }
}
